import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { apiClient, unwrapApiResult, withAbortSignal } from "@/lib/api-client";
import type { DbProduct, DbCategory, DbProductImage, DbProductVariant, Product } from "@/types/product";
import { hasStarvieBalticsOverride, STARVIE_BALTICS_VIRTUAL_QTY } from "@/lib/starvie-baltics-override";

const STALE_TIME = 30 * 1000;
const PRODUCT_FULL_SELECT = "id,name,slug,brand,price,compare_at_price,currency,stock,stock_spain,stock_nuestro,stock_tennispro,sku,status,category_id,description,specs,product_type,same_day_delivery,availability_source,created_at,updated_at,show_on_sale,is_kids";
const PRODUCT_CARD_SELECT = "id,name,slug,brand,price,compare_at_price,currency,stock,stock_spain,stock_nuestro,stock_tennispro,sku,status,category_id,specs,product_type,same_day_delivery,availability_source,created_at,show_on_sale,is_kids";

const CATEGORY_MIN_SELECT = "id,name,name_en,name_es,slug,product_type,sort_order,is_active,created_at";

interface UseDbProductsOptions {
  includeAll?: boolean;
  limit?: number;
  page?: number;
  cardFieldsOnly?: boolean;
  enabled?: boolean;
}

export function useDbProducts(options?: UseDbProductsOptions) {
  const limit = options?.limit;
  const page = options?.page ?? 0;

  return useQuery({
    queryKey: [
      "products",
      options?.includeAll ? "all" : "active",
      options?.cardFieldsOnly ? "card" : "full",
      limit ?? "unlimited",
      page,
    ],
    queryFn: async ({ signal }) => {
      const result = await apiClient.query<any[]>({
        label: "products",
        routeTag: "useDbProducts",
        timeoutMs: options?.includeAll ? 25_000 : 15_000,
        signal,
        run: (abortSignal) => {
          let query = withAbortSignal(
            supabase
              .from("products")
              .select(options?.cardFieldsOnly ? PRODUCT_CARD_SELECT : PRODUCT_FULL_SELECT)
              .order("created_at", { ascending: false }),
            abortSignal
          );

          if (!options?.includeAll) {
            query = query.eq("status", "active");
          }

          if (typeof limit === "number" && limit > 0) {
            const from = page * limit;
            const to = from + limit - 1;
            query = query.range(from, to);
          }

          return query;
        },
      });

      return (unwrapApiResult(result) ?? []) as DbProduct[];
    },
    enabled: options?.enabled ?? true,
    staleTime: STALE_TIME,
    retry: 2,
    retryDelay: 1000,
  });
}

export function useDbProduct(id: string) {
  return useQuery({
    queryKey: ["product", id],
    queryFn: async ({ signal }) => {
      const result = await apiClient.query<any | null>({
        label: "product-by-id",
        routeTag: "useDbProduct",
        timeoutMs: 15_000,
        signal,
        run: (abortSignal) =>
          withAbortSignal(
            supabase.from("products").select("*").eq("id", id).maybeSingle(),
            abortSignal
          ),
      });

      return unwrapApiResult(result) as DbProduct | null;
    },
    enabled: !!id,
    staleTime: STALE_TIME,
    retry: 2,
    retryDelay: 1000,
  });
}

export function useDbProductBySlug(slug: string) {
  return useQuery({
    queryKey: ["product-slug", slug],
    queryFn: async ({ signal }) => {
      const result = await apiClient.query<any | null>({
        label: "product-by-slug",
        routeTag: "useDbProductBySlug",
        timeoutMs: 15_000,
        signal,
        run: (abortSignal) =>
          withAbortSignal(
            supabase
              .from("products")
              .select("*")
              .eq("slug", slug)
              .eq("status", "active")
              .maybeSingle(),
            abortSignal
          ),
      });

      return unwrapApiResult(result) as DbProduct | null;
    },
    enabled: !!slug,
    staleTime: STALE_TIME,
    retry: 2,
    retryDelay: 1000,
  });
}

export function useDbProductByPrivateToken(token: string) {
  return useQuery({
    queryKey: ["product-private-token", token],
    queryFn: async ({ signal }) => {
      const result = await apiClient.query<any | null>({
        label: "product-by-private-token",
        routeTag: "useDbProductByPrivateToken",
        timeoutMs: 15_000,
        signal,
        run: async () => {
          const res = await supabase.rpc("get_product_by_private_token", { _token: token });
          return { data: Array.isArray(res.data) ? (res.data[0] ?? null) : res.data, error: res.error };
        },
      });
      return unwrapApiResult(result) as DbProduct | null;
    },
    enabled: !!token,
    staleTime: STALE_TIME,
    retry: 1,
  });
}

export function useDbCategories(productType?: string, options?: { minimalFields?: boolean; enabled?: boolean }) {
  const minimalFields = options?.minimalFields ?? true;

  return useQuery({
    queryKey: ["categories", productType ?? "all", minimalFields ? "minimal" : "full"],
    queryFn: async ({ signal }) => {
      const result = await apiClient.query<any[]>({
        label: "categories",
        routeTag: "useDbCategories",
        timeoutMs: 15_000,
        signal,
        run: (abortSignal) => {
          let query = withAbortSignal(
            supabase
              .from("categories")
              .select(minimalFields ? CATEGORY_MIN_SELECT : "*")
              .eq("is_active", true)
              .order("sort_order"),
            abortSignal
          );

          if (productType) {
            query = query.eq("product_type", productType);
          }

          return query;
        },
      });

      return (unwrapApiResult(result) ?? []) as DbCategory[];
    },
    enabled: options?.enabled ?? true,
    staleTime: STALE_TIME,
    retry: 2,
    retryDelay: 1000,
  });
}

export function useDbAllCategories() {
  return useQuery({
    queryKey: ["categories", "admin-all"],
    queryFn: async ({ signal }) => {
      const result = await apiClient.query<any[]>({
        label: "all-categories",
        routeTag: "useDbAllCategories",
        timeoutMs: 25_000,
        signal,
        run: (abortSignal) =>
          withAbortSignal(
            supabase.from("categories").select("*").order("product_type").order("sort_order"),
            abortSignal
          ),
      });

      return (unwrapApiResult(result) ?? []) as DbCategory[];
    },
    staleTime: STALE_TIME,
    retry: 2,
    retryDelay: 1000,
  });
}

export function useDbProductImages(productId: string) {
  return useQuery({
    queryKey: ["product-images", productId],
    queryFn: async ({ signal }) => {
      const result = await apiClient.query<any[]>({
        label: "product-images",
        routeTag: "useDbProductImages",
        timeoutMs: 15_000,
        signal,
        run: (abortSignal) =>
          withAbortSignal(
            supabase.from("product_images").select("*").eq("product_id", productId).order("sort_order"),
            abortSignal
          ),
      });

      return (unwrapApiResult(result) ?? []) as DbProductImage[];
    },
    enabled: !!productId,
    staleTime: STALE_TIME,
    retry: 2,
    retryDelay: 1000,
  });
}

export function useDbProductImagesByProductIds(productIds: string[], options?: { enabled?: boolean }) {
  const ids = Array.from(new Set(productIds.filter(Boolean))).sort();
  // Build a stable per-id-set key so multiple concurrent callers
  // (Newest/Popular/Featured/Products page…) don't abort each other via the
  // shared `latestControllers` dedup keyed on routeTag+label.
  const idsHash = ids.length
    ? `${ids.length}:${ids[0].slice(0, 8)}:${ids[ids.length - 1].slice(0, 8)}`
    : "empty";

  return useQuery({
    queryKey: ["product-images", "by-ids", ids],
    queryFn: async ({ signal }) => {
      const idChunkSize = 50;
      const pageSize = 1000;

      const chunks: string[][] = [];
      for (let chunkStart = 0; chunkStart < ids.length; chunkStart += idChunkSize) {
        chunks.push(ids.slice(chunkStart, chunkStart + idChunkSize));
      }

      // Fetch chunks in parallel — sequential round-trips were the main delay
      // before any <img> could even mount on large grids.
      const results = await Promise.all(
        chunks.map(async (idChunk, chunkIndex) => {
          const rows: any[] = [];
          for (let from = 0; ; from += pageSize) {
            const result = await apiClient.query<any[]>({
              label: `product-images-by-ids:${idsHash}:${chunkIndex}:${from}`,
              routeTag: "useDbProductImagesByProductIds",
              timeoutMs: 15_000,
              signal,
              run: (abortSignal) =>
                withAbortSignal(
                  supabase
                    .from("product_images")
                    .select("*")
                    .in("product_id", idChunk)
                    .order("product_id", { ascending: true })
                    .order("sort_order", { ascending: true })
                    .range(from, from + pageSize - 1),
                  abortSignal
                ),
            });
            const page = (unwrapApiResult(result) ?? []) as any[];
            rows.push(...page);
            if (page.length < pageSize) break;
          }
          return rows;
        })
      );

      return results.flat() as DbProductImage[];
    },

    enabled: (options?.enabled ?? true) && ids.length > 0,
    staleTime: STALE_TIME,
    retry: 2,
    retryDelay: 1000,
  });
}

export function useAllProductImages(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["all-product-images"],
    queryFn: async ({ signal }) => {
      const result = await apiClient.query<any[]>({
        label: "all-product-images",
        routeTag: "useAllProductImages",
        timeoutMs: 15_000,
        signal,
        run: (abortSignal) =>
          withAbortSignal(
            supabase.from("product_images").select("*").order("sort_order"),
            abortSignal
          ),
      });

      return (unwrapApiResult(result) ?? []) as DbProductImage[];
    },
    enabled: options?.enabled ?? true,
    staleTime: STALE_TIME,
    retry: 2,
    retryDelay: 1000,
  });
}

export function useDbProductVariants(productId: string) {
  return useQuery({
    queryKey: ["product-variants", productId],
    queryFn: async ({ signal }) => {
      const result = await apiClient.query<any[]>({
        label: "product-variants",
        routeTag: "useDbProductVariants",
        timeoutMs: 15_000,
        signal,
        run: (abortSignal) =>
          withAbortSignal(
            supabase.from("product_variants").select("*").eq("product_id", productId).order("sort_order"),
            abortSignal
          ),
      });

      return (unwrapApiResult(result) ?? []) as DbProductVariant[];
    },
    enabled: !!productId,
    staleTime: STALE_TIME,
    retry: 2,
    retryDelay: 1000,
  });
}

// Fetch aggregated variant stock per product for availability calculation
export function useVariantStockSummary() {
  return useQuery({
    queryKey: ["variant-stock-summary"],
    queryFn: async ({ signal }) => {
      // Paginate — Supabase caps rows per response at 1000. Shoes alone produce
      // several thousand variants, so a single request would silently truncate
      // and make most shoe products appear as "contact for availability".
      const pageSize = 1000;
      const rows: Array<{ product_id: string; stock: number; stock_spain: number | null; is_active: boolean }> = [];
      for (let from = 0; ; from += pageSize) {
        const result = await apiClient.query<any[]>({
          label: "variant-stock-summary",
          routeTag: "useVariantStockSummary",
          timeoutMs: 15_000,
          signal,
          run: (abortSignal) =>
            withAbortSignal(
              supabase
                .from("product_variants")
                .select("product_id, stock, stock_spain, is_active")
                .eq("is_active", true)
                .order("product_id", { ascending: true })
                .order("id", { ascending: true })
                .range(from, from + pageSize - 1),
              abortSignal
            ),
        });
        const page = (unwrapApiResult(result) ?? []) as typeof rows;
        rows.push(...page);
        if (page.length < pageSize) break;
      }

      const map = new Map<string, { totalStock: number; totalStockSpain: number }>();
      for (const r of rows) {
        const cur = map.get(r.product_id) ?? { totalStock: 0, totalStockSpain: 0 };
        cur.totalStock += r.stock || 0;
        cur.totalStockSpain += r.stock_spain ?? 0;
        map.set(r.product_id, cur);
      }
      return map;
    },
    staleTime: STALE_TIME,
    retry: 2,
    retryDelay: 1000,
  });
}

export type VariantStockMap = Map<string, { totalStock: number; totalStockSpain: number }>;

// Available sizes per product (variant_type = 'size') where at least one warehouse has stock.
// Used for size filters (e.g. shoes) in listing pages.
export function useProductSizeAvailability() {
  return useQuery({
    queryKey: ["product-size-availability"],
    queryFn: async ({ signal }) => {
      const pageSize = 1000;
      const rows: Array<{ product_id: string; option: string; stock: number; stock_spain: number | null }> = [];
      for (let from = 0; ; from += pageSize) {
        const result = await apiClient.query<any[]>({
          label: "product-size-availability",
          routeTag: "useProductSizeAvailability",
          timeoutMs: 15_000,
          signal,
          run: (abortSignal) =>
            withAbortSignal(
              supabase
                .from("product_variants")
                .select("product_id, option, stock, stock_spain, is_active, variant_type, id")
                .eq("is_active", true)
                .eq("variant_type", "size")
                .order("product_id", { ascending: true })
                .order("id", { ascending: true })
                .range(from, from + pageSize - 1),
              abortSignal
            ),
        });
        const page = (unwrapApiResult(result) ?? []) as typeof rows;
        rows.push(...page);
        if (page.length < pageSize) break;
      }
      const map = new Map<string, Set<string>>();
      const allSizes = new Set<string>();
      for (const r of rows) {
        const hasStock = (r.stock || 0) > 0 || (r.stock_spain ?? 0) > 0;
        if (!hasStock) continue;
        const opt = (r.option || "").trim();
        if (!opt) continue;
        allSizes.add(opt);
        const cur = map.get(r.product_id) ?? new Set<string>();
        cur.add(opt);
        map.set(r.product_id, cur);
      }
      return { productSizes: map, allSizes };
    },
    staleTime: STALE_TIME,
    retry: 2,
    retryDelay: 1000,
  });
}


// Convert DB product + images + category to the legacy Product type for cart compatibility
export function toCartProduct(
  p: DbProduct,
  images: DbProductImage[],
  categories: DbCategory[],
  variantStockMap?: VariantStockMap
): Product {
  const cat = categories.find((c) => c.id === p.category_id);

  // Determine availability: check product-level stock first, then variant-level
  const variantSummary = variantStockMap?.get(p.id);
  const hasVariants = !!variantSummary;
  const localStock = hasVariants
    ? (p.stock || 0) + variantSummary.totalStock
    : p.stock;
  // Baltics signal: stock_spain (numeric) OR stock_nuestro (binary 1/0) OR stock_tennispro (numeric). Variants only carry stock_spain.
  const baseSpain = (p.stock_spain ?? 0) + ((p.stock_nuestro ?? 0) > 0 ? 1 : 0) + (p.stock_tennispro ?? 0);
  const spainStock = hasVariants
    ? (baseSpain + variantSummary.totalStockSpain)
    : baseSpain;

  let availabilitySource: "local" | "europe" | "contact";
  if (localStock > 0) {
    availabilitySource = "local";
  } else if (spainStock > 0) {
    availabilitySource = "europe";
  } else {
    availabilitySource = "contact";
  }

  // Shoes are stocked in the Baltics warehouse — always surface as "europe"
  // whenever any size (variant) has stock, regardless of which stock column
  // it lives in.
  if (p.product_type === "shoes" && availabilitySource === "local") {
    availabilitySource = "europe";
  }

  // StarVie-only override: admin-selected "Noliktavā Baltijā" keeps the product
  // available/purchasable even at 0 stock. Scoped to StarVie + europe only.
  const starvieOverride = hasStarvieBalticsOverride(p.brand, (p as any).availability_source);
  if (starvieOverride) {
    availabilitySource = "europe";
  }

  const isPrivate = (p as any).is_private === true;
  const privateToken = (p as any).private_token as string | null | undefined;
  const cartSlug = isPrivate && privateToken ? `p/${privateToken}` : p.slug;

  return {
    id: p.id,
    name: p.name,
    slug: cartSlug,
    description: p.description || "",
    category: cat?.name || "",
    categorySlug: cat?.slug || "",
    category_id: p.category_id || undefined,
    brand: p.brand || "",
    price: Number(p.price),
    compareAtPrice: p.compare_at_price ? Number(p.compare_at_price) : undefined,
    currency: p.currency,
    stock: starvieOverride ? Math.max(p.stock ?? 0, STARVIE_BALTICS_VIRTUAL_QTY) : p.stock,
    sku: p.sku || "",
    specs: (p.specs as Record<string, string>) || {},
    images: images.map((i) => i.url),
    status: p.status as "active" | "draft",
    product_type: p.product_type || "racket",
    sameDayDelivery: p.same_day_delivery ?? false,
    availabilitySource,
    isKids: (p as any).is_kids === true,
  };
}

